import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useOffline } from '@/contexts/OfflineContext';
import { getIncidents, createIncident, updateIncident, runIncidentAIAnalysis, downloadIncidentPDF, mapIncidentToECCAIRS, exportECCAIRSFormat, getECCAIRSTaxonomy } from '@/services/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { 
  Plus, Search, Filter, FileWarning, Brain, Download, Eye, Edit,
  ChevronLeft, Sparkles, AlertTriangle, Calendar, MessageSquare, Globe, FileJson, Plane,
  WifiOff, CloudOff, Cloud, Mic
} from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import CollaborationPanel from '@/components/CollaborationPanel';
import AssetSelector, { AssetDisplay } from '@/components/AssetSelector';
import VoiceIncidentRecorder from '@/components/VoiceIncidentRecorder';
import PhotoCapture from '@/components/PhotoCapture';
import HelpTooltip from '@/components/help/HelpTooltip';
import AIFieldSuggestions from '@/components/AIFieldSuggestions';
import PolishButton from '@/components/PolishButton';

const OCCURRENCE_CATEGORIES = [
  'AIRPROX', 'ARC', 'BIRD', 'CABIN', 'CFIT', 'F-NI', 'FUEL', 'GCOL', 
  'ICE', 'LOC-I', 'LOC-G', 'MAC', 'MED', 'OTHR', 'RE', 'RI', 
  'SCF-NP', 'SCF-PP', 'SEC', 'TURB', 'USOS', 'WSTRW'
];

const SEVERITIES = ['catastrophic', 'hazardous', 'major', 'minor', 'negligible'];
const LIKELIHOODS = ['frequent', 'occasional', 'remote', 'improbable', 'extremely_improbable'];
const STATUSES = ['submitted', 'under_review', 'investigating', 'mitigating', 'closed'];

const STATUS_COLORS = {
  submitted: 'bg-blue-900/30 text-blue-400 border-blue-900/50',
  under_review: 'bg-yellow-900/30 text-yellow-400 border-yellow-900/50',
  investigating: 'bg-orange-900/30 text-orange-400 border-orange-900/50',
  mitigating: 'bg-violet-900/30 text-violet-400 border-violet-900/50',
  closed: 'bg-emerald-900/30 text-emerald-400 border-emerald-900/50',
};

const RISK_COLORS = {
  critical: 'risk-critical',
  high: 'risk-high',
  medium: 'risk-medium',
  low: 'risk-low',
  acceptable: 'risk-acceptable',
};

export default function IncidentsPage({ initialMode }) {
  const { t } = useTranslation();
  const { api, token, user } = useAuth();
  const { isOnline, saveIncidentOffline, isInitialized, syncStats } = useOffline();
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState(initialMode || 'list'); // list, create, view, edit
  const [selectedIncident, setSelectedIncident] = useState(null);
  const [filters, setFilters] = useState({ status: '', severity: '', category: '' });
  const [searchTerm, setSearchTerm] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [showCollaboration, setShowCollaboration] = useState(true);
  const [eccairsLoading, setEccairsLoading] = useState(false);
  const [eccairsTaxonomy, setEccairsTaxonomy] = useState(null);
  const [voiceOpen, setVoiceOpen] = useState(false);

  const applyVoiceExtraction = (extracted) => {
    if (!extracted) return;
    setFormData(prev => ({
      ...prev,
      title: extracted.title || prev.title,
      description: extracted.description || prev.description,
      location: extracted.location || prev.location,
      occurrence_date: extracted.occurrence_date || prev.occurrence_date,
      occurrence_category: extracted.occurrence_category || prev.occurrence_category,
      flight_number: extracted.flight_number || prev.flight_number,
      aircraft_type: extracted.aircraft_type || prev.aircraft_type,
      aircraft_registration: extracted.aircraft_registration || prev.aircraft_registration,
    }));
  };

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    location: '',
    occurrence_date: new Date().toISOString().split('T')[0],
    occurrence_category: 'OTHR',
    is_anonymous: false,
    flight_number: '',
    aircraft_type: '',
    aircraft_registration: '',
    // Asset linking fields
    aircraft_id: null,
    engine_id: null,
    component_id: null,
    // Photo evidence (works offline — stored as base64 in IndexedDB queue)
    attachments: [],
    // Per-photo GPS metadata, parallel to attachments[]
    attachment_geo: [],
  });

  useEffect(() => {
    if (mode === 'list') {
      fetchIncidents();
    }
  }, [mode, filters]);

  const fetchIncidents = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.status) params.status = filters.status;
      if (filters.severity) params.severity = filters.severity;
      if (filters.category) params.category = filters.category;
      
      const response = await getIncidents(api, params);
      setIncidents(response.data);
    } catch (error) {
      console.error('Failed to fetch incidents:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch ECCAIRS taxonomy
  const fetchECCAIRSTaxonomy = async () => {
    try {
      const response = await getECCAIRSTaxonomy(api);
      setEccairsTaxonomy(response.data);
    } catch (error) {
      console.error('Failed to fetch ECCAIRS taxonomy:', error);
    }
  };

  // Map incident to ECCAIRS 2
  const handleMapToECCAIRS = async (incidentId) => {
    setEccairsLoading(true);
    try {
      const response = await mapIncidentToECCAIRS(api, incidentId);
      toast.success('ECCAIRS mapping created successfully');
      // Update selected incident with ECCAIRS data
      if (selectedIncident) {
        setSelectedIncident({ 
          ...selectedIncident, 
          eccairs_category: response.data.eccairs_categories[0],
          eccairs_data: response.data.eccairs_data 
        });
      }
    } catch (error) {
      console.error('Failed to map to ECCAIRS:', error);
      toast.error('Failed to create ECCAIRS mapping');
    } finally {
      setEccairsLoading(false);
    }
  };

  // Export ECCAIRS format
  const handleExportECCAIRS = async (incidentId) => {
    try {
      const response = await exportECCAIRSFormat(api, incidentId);
      // Download as JSON
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `eccairs_${selectedIncident?.report_number || incidentId}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('ECCAIRS export downloaded');
    } catch (error) {
      console.error('Failed to export ECCAIRS:', error);
      toast.error('Failed to export ECCAIRS format');
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        occurrence_date: new Date(formData.occurrence_date).toISOString()
      };
      
      if (isOnline) {
        // Online: Submit directly to server
        await createIncident(api, payload);
        toast.success('Incident created successfully');
      } else {
        // Offline: Save locally for later sync
        if (isInitialized) {
          await saveIncidentOffline(payload);
          toast.success(
            <div className="flex items-center gap-2">
              <CloudOff className="h-4 w-4" />
              <span>Incident saved offline. Will sync when online.</span>
            </div>
          );
        } else {
          toast.error('Offline storage not available. Please try again when online.');
          return;
        }
      }
      setMode('list');
      resetForm();
    } catch (error) {
      console.error('Failed to create incident:', error);
      toast.error('Failed to create incident');
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      await updateIncident(api, selectedIncident.id, formData);
      toast.success('Incident updated successfully');
      await fetchIncidents(); // Refresh the list
      setMode('list');
      resetForm();
    } catch (error) {
      console.error('Failed to update incident:', error);
      toast.error('Failed to update incident');
    }
  };

  const handleRunAI = async (incidentId) => {
    setAiLoading(true);
    try {
      const response = await runIncidentAIAnalysis(api, incidentId);
      // Refresh incident details
      if (selectedIncident) {
        setSelectedIncident({ ...selectedIncident, ai_classification: response.data });
      }
      fetchIncidents();
    } catch (error) {
      console.error('Failed to run AI analysis:', error);
    } finally {
      setAiLoading(false);
    }
  };

  const handleDownloadPDF = async (incidentId) => {
    try {
      const response = await downloadIncidentPDF(token, incidentId);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `incident_${incidentId}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download PDF:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      location: '',
      occurrence_date: new Date().toISOString().split('T')[0],
      occurrence_category: 'OTHR',
      is_anonymous: false,
      flight_number: '',
      aircraft_type: '',
      aircraft_registration: '',
      aircraft_id: null,
      engine_id: null,
      component_id: null,
      attachments: [],
      attachment_geo: [],
    });
    setSelectedIncident(null);
  };

  const openViewMode = (incident) => {
    setSelectedIncident(incident);
    setMode('view');
  };

  const openEditMode = (incident) => {
    setSelectedIncident(incident);
    setFormData({
      // Preserve all existing fields
      title: incident.title || '',
      description: incident.description || '',
      location: incident.location || '',
      occurrence_date: incident.occurrence_date?.split('T')[0] || new Date().toISOString().split('T')[0],
      occurrence_category: incident.occurrence_category || 'OTHR',
      is_anonymous: incident.is_anonymous || false,
      flight_number: incident.flight_number || '',
      aircraft_type: incident.aircraft_type || '',
      aircraft_registration: incident.aircraft_registration || '',
      aircraft_id: incident.aircraft_id || null,
      engine_id: incident.engine_id || null,
      component_id: incident.component_id || null,
      // Investigation fields
      status: incident.status || 'open',
      severity: incident.severity || '',
      likelihood: incident.likelihood || '',
      investigation_notes: incident.investigation_notes || '',
      root_cause: incident.root_cause || '',
      attachments: incident.attachments || [],
      attachment_geo: incident.attachment_geo || [],
    });
    setMode('edit');
  };

  const filteredIncidents = incidents.filter(incident => 
    incident.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    incident.report_number?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // List View
  if (mode === 'list') {
    return (
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-white font-barlow tracking-tight" data-testid="incidents-title">
              {t('incidents.title')}
            </h1>
            <p className="text-slate-400 mt-1">Manage safety incident reports</p>
          </div>
          <div className="flex items-center gap-3">
            {/* Offline Sync Status */}
            {!isOnline && (
              <Badge variant="outline" className="flex items-center gap-1.5 bg-amber-900/30 border-amber-700 text-amber-400">
                <WifiOff className="h-3.5 w-3.5" />
                Offline Mode
              </Badge>
            )}
            {syncStats.pending > 0 && (
              <Badge variant="outline" className="flex items-center gap-1.5 bg-blue-900/30 border-blue-700 text-blue-400">
                <Cloud className="h-3.5 w-3.5" />
                {syncStats.pending} pending sync
              </Badge>
            )}
            <Button
              type="button"
              onClick={() => { setMode('create'); setVoiceOpen(true); }}
              variant="outline"
              className="border-rose-500/50 text-rose-300 hover:bg-rose-500/10 hover:text-rose-200"
              data-testid="voice-file-incident-btn"
              title="Speak your report — the AI will fill in the form for you"
            >
              <Mic className="w-4 h-4 mr-2" />
              File by Voice
            </Button>
            <Button
              onClick={() => setMode('create')}
              className="bg-sky-600 hover:bg-sky-700"
              data-testid="new-incident-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              {t('incidents.newIncident')}
            </Button>
          </div>
        </div>

        {/* Filters */}
        <Card className="bg-slate-900/60 border-slate-800">
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-4">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <Input
                    placeholder="Search incidents..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 bg-slate-950 border-slate-800"
                    data-testid="incident-search"
                  />
                </div>
              </div>
              <Select value={filters.status || "all"} onValueChange={(v) => setFilters({ ...filters, status: v === "all" ? "" : v })}>
                <SelectTrigger className="w-[150px] bg-slate-950 border-slate-800" data-testid="filter-status">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-800">
                  <SelectItem value="all">All Statuses</SelectItem>
                  {STATUSES.map(s => (
                    <SelectItem key={s} value={s}>{t(`incidents.statuses.${s}`)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filters.severity || "all"} onValueChange={(v) => setFilters({ ...filters, severity: v === "all" ? "" : v })}>
                <SelectTrigger className="w-[150px] bg-slate-950 border-slate-800" data-testid="filter-severity">
                  <SelectValue placeholder="Severity" />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-800">
                  <SelectItem value="all">All Severities</SelectItem>
                  {SEVERITIES.map(s => (
                    <SelectItem key={s} value={s}>{t(`incidents.severities.${s}`)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Incidents Table */}
        <Card className="bg-slate-900/60 border-slate-800">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="spinner w-8 h-8" />
              </div>
            ) : filteredIncidents.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <FileWarning className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>{t('common.noData')}</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-800 hover:bg-transparent">
                    <TableHead className="text-slate-400">{t('incidents.reportNumber')}</TableHead>
                    <TableHead className="text-slate-400">Title</TableHead>
                    <TableHead className="text-slate-400">{t('incidents.category')}</TableHead>
                    <TableHead className="text-slate-400">{t('common.status')}</TableHead>
                    <TableHead className="text-slate-400">{t('incidents.riskLevel')}</TableHead>
                    <TableHead className="text-slate-400">{t('incidents.occurrenceDate')}</TableHead>
                    <TableHead className="text-slate-400">{t('common.actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredIncidents.map((incident) => (
                    <TableRow key={incident.id} className="border-slate-800 hover:bg-slate-800/50" data-testid={`incident-row-${incident.id}`}>
                      <TableCell className="font-mono text-sky-400">{incident.report_number}</TableCell>
                      <TableCell className="text-white max-w-[200px] truncate">
                        {incident.title || <span className="text-slate-500 italic">Untitled</span>}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="border-slate-700 text-slate-300">
                          {incident.occurrence_category}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={`${STATUS_COLORS[incident.status]} border`}>
                          {t(`incidents.statuses.${incident.status}`)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {incident.risk_level ? (
                          <Badge className={`${RISK_COLORS[incident.risk_level]} px-2.5 py-0.5 rounded-sm text-xs font-medium`}>
                            {t(`risks.levels.${incident.risk_level}`)}
                          </Badge>
                        ) : (
                          <span className="text-slate-500">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-slate-400">
                        {format(new Date(incident.occurrence_date), 'yyyy-MM-dd')}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openViewMode(incident)}
                            className="text-slate-400 hover:text-white"
                            data-testid={`view-incident-${incident.id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openEditMode(incident)}
                            className="text-slate-400 hover:text-white"
                            data-testid={`edit-incident-${incident.id}`}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDownloadPDF(incident.id)}
                            className="text-slate-400 hover:text-white"
                            data-testid={`download-incident-${incident.id}`}
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Create View
  if (mode === 'create') {
    return (
      <div className="p-6 space-y-6">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            onClick={() => { setMode('list'); resetForm(); }}
            className="text-slate-400 hover:text-white"
            data-testid="back-to-list"
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <h1 className="text-2xl font-bold text-white font-barlow">
            {t('incidents.newIncident')}
          </h1>
          <Button
            type="button"
            onClick={() => setVoiceOpen(true)}
            className="ml-auto bg-rose-600 hover:bg-rose-500"
            data-testid="voice-record-open-btn"
          >
            <Mic className="w-4 h-4 mr-2" />
            {t('voiceIncident.startRecording', 'Voice Report')}
          </Button>
        </div>

        <VoiceIncidentRecorder
          open={voiceOpen}
          onClose={() => setVoiceOpen(false)}
          onApply={applyVoiceExtraction}
        />

        <Card className="bg-slate-900/60 border-slate-800 max-w-3xl">
          <CardContent className="p-6">
            <form onSubmit={handleCreate} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Label className="text-slate-300">Title *</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    required
                    className="bg-slate-950 border-slate-800 mt-1"
                    placeholder="Brief description of the incident"
                    data-testid="incident-title-input"
                  />
                </div>

                <div className="md:col-span-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-slate-300">{t('incidents.description')} *</Label>
                    <PolishButton
                      text={formData.description}
                      kind="incident"
                      token={token}
                      onApply={(polished) => setFormData((prev) => ({ ...prev, description: polished }))}
                    />
                  </div>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    required
                    rows={4}
                    className="bg-slate-950 border-slate-800 mt-1"
                    placeholder="Detailed description of what happened..."
                    data-testid="incident-description-input"
                  />
                  <AIFieldSuggestions
                    title={formData.title}
                    description={formData.description}
                    token={token}
                    onApply={(patch) => setFormData((prev) => ({
                      ...prev,
                      ...(patch.occurrence_category && { occurrence_category: patch.occurrence_category }),
                      ...(patch.severity && { severity: patch.severity }),
                      ...(patch.likelihood && { likelihood: patch.likelihood }),
                      ...(patch.contributing_factors && { contributing_factors: patch.contributing_factors }),
                    }))}
                  />
                </div>

                <div>
                  <Label className="text-slate-300">{t('incidents.occurrenceDate')} *</Label>
                  <Input
                    type="date"
                    value={formData.occurrence_date}
                    onChange={(e) => setFormData({ ...formData, occurrence_date: e.target.value })}
                    required
                    className="bg-slate-950 border-slate-800 mt-1"
                    data-testid="incident-date-input"
                  />
                </div>

                <div>
                  <Label className="text-slate-300">{t('incidents.location')} *</Label>
                  <Input
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    required
                    className="bg-slate-950 border-slate-800 mt-1"
                    placeholder="e.g., Runway 27L, Gate 5"
                    data-testid="incident-location-input"
                  />
                </div>

                <div>
                  <Label className="text-slate-300 inline-flex items-center">
                    {t('incidents.category')} *
                    <HelpTooltip
                      label="Occurrence Category (ICAO ADREP)"
                      body={"The type of safety occurrence, using the ICAO ADREP 3-letter code.\n\nCommon examples:\n• BIRD — bird / wildlife strike\n• RI — runway incursion\n• TURB — turbulence\n• MED — medical event\n• OTHR — none of the above\n\nPick the closest match. You can refine later during investigation."}
                      articleId="task-file-incident"
                      example="BIRD"
                      onApplyExample={(v) => setFormData((p) => ({ ...p, occurrence_category: v }))}
                    />
                  </Label>
                  <Select 
                    value={formData.occurrence_category} 
                    onValueChange={(v) => setFormData({ ...formData, occurrence_category: v })}
                  >
                    <SelectTrigger className="bg-slate-950 border-slate-800 mt-1" data-testid="incident-category-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-800 max-h-[300px]">
                      {OCCURRENCE_CATEGORIES.map(cat => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-slate-300 inline-flex items-center">
                    {t('incidents.flightNumber')}
                    <HelpTooltip
                      label="Flight Number"
                      body={"Your operator's IATA/ICAO flight designator.\n\nFormat: 2-letter IATA airline code + up to 4 digits.\n• SV123 — Saudia\n• SVA789 — Saudia (ICAO 3-letter form)\n• EK47 — Emirates\n\nLeave blank if this happened on a positioning / ferry flight."}
                      example="SV123"
                      onApplyExample={(v) => setFormData({ ...formData, flight_number: v })}
                    />
                  </Label>
                  <Input
                    value={formData.flight_number}
                    onChange={(e) => setFormData({ ...formData, flight_number: e.target.value })}
                    className="bg-slate-950 border-slate-800 mt-1"
                    placeholder="e.g., SV123"
                    data-testid="incident-flight-input"
                  />
                </div>

                {/* Asset Selector - Link to Aircraft/Engine/Component */}
                <div className="md:col-span-2">
                  <Card className="bg-slate-800/50 border-slate-700">
                    <CardContent className="p-4">
                      <AssetSelector
                        value={{
                          aircraft_id: formData.aircraft_id,
                          aircraft_registration: formData.aircraft_registration,
                          engine_id: formData.engine_id,
                          component_id: formData.component_id,
                        }}
                        onChange={(assetData) => {
                          setFormData({
                            ...formData,
                            aircraft_id: assetData.aircraft_id,
                            aircraft_registration: assetData.aircraft_registration || formData.aircraft_registration,
                            aircraft_type: assetData.aircraft_type || formData.aircraft_type,
                            engine_id: assetData.engine_id,
                            component_id: assetData.component_id,
                          });
                        }}
                      />
                    </CardContent>
                  </Card>
                </div>

                {/* Manual aircraft entry (if not using asset selector) */}
                <div>
                  <Label className="text-slate-300">{t('incidents.aircraftType')}</Label>
                  <Input
                    value={formData.aircraft_type}
                    onChange={(e) => setFormData({ ...formData, aircraft_type: e.target.value })}
                    className="bg-slate-950 border-slate-800 mt-1"
                    placeholder="e.g., Boeing 787-9"
                    data-testid="incident-aircraft-type-input"
                  />
                  <p className="text-xs text-slate-500 mt-1">Auto-filled when aircraft selected above</p>
                </div>

                <div>
                  <Label className="text-slate-300">{t('incidents.aircraftRegistration')}</Label>
                  <Input
                    value={formData.aircraft_registration}
                    onChange={(e) => setFormData({ ...formData, aircraft_registration: e.target.value })}
                    className="bg-slate-950 border-slate-800 mt-1"
                    placeholder="e.g., HZ-ARD"
                    data-testid="incident-registration-input"
                  />
                  <p className="text-xs text-slate-500 mt-1">Auto-filled when aircraft selected above</p>
                </div>

                <div className="md:col-span-2 flex items-center gap-3">
                  <Checkbox
                    id="anonymous"
                    checked={formData.is_anonymous}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_anonymous: checked })}
                    data-testid="incident-anonymous-checkbox"
                  />
                  <Label htmlFor="anonymous" className="text-slate-300 cursor-pointer inline-flex items-center">
                    {t('incidents.anonymous')}
                    <HelpTooltip
                      label="Anonymous submission"
                      body={"When checked, your name is NOT stored with the report.\n\nUse this for voluntary safety reports where you want the safety team to see the issue but not know who filed it.\n\nJust-Culture rule: honest anonymous reports are never used against you."}
                      articleId="task-file-incident"
                    />
                  </Label>
                </div>

                {/* Photo evidence — base64 in attachments[], queued in IndexedDB when offline */}
                <div className="md:col-span-2 space-y-2" data-testid="incident-photos-section">
                  <Label className="text-slate-300">Photo Evidence (optional, max 5)</Label>
                  <p className="text-xs text-slate-500">
                    Photos are compressed locally, GPS-tagged at capture time, and saved with the report — they sync automatically when reconnected.
                  </p>
                  <PhotoCapture
                    value={formData.attachments || []}
                    onChange={(attachments) => setFormData({ ...formData, attachments })}
                    geoTag={true}
                    geoValue={formData.attachment_geo || []}
                    onGeoChange={(attachment_geo) => setFormData({ ...formData, attachment_geo })}
                    testId="incident-photos"
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <Button type="submit" className="bg-sky-600 hover:bg-sky-700" data-testid="submit-incident-btn">
                  {t('common.submit')}
                </Button>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => { setMode('list'); resetForm(); }}
                  className="border-slate-700 text-slate-300"
                >
                  {t('common.cancel')}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // View/Edit Mode
  if (mode === 'view' || mode === 'edit') {
    const incident = selectedIncident;
    
    return (
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => { setMode('list'); resetForm(); }}
              className="text-slate-400 hover:text-white"
            >
              <ChevronLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-white font-barlow" data-testid="incident-detail-title">
                {incident.report_number}
              </h1>
              <p className="text-slate-400">{incident.title}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => handleRunAI(incident.id)}
              disabled={aiLoading}
              className="border-violet-600/50 text-violet-400 hover:bg-violet-600/20"
              data-testid="run-ai-analysis-btn"
            >
              {aiLoading ? (
                <span className="flex items-center gap-2">
                  <span className="spinner w-4 h-4" />
                  Analyzing...
                </span>
              ) : (
                <>
                  <Brain className="w-4 h-4 mr-2" />
                  {t('incidents.runAiAnalysis')}
                </>
              )}
            </Button>
            <Button
              variant="outline"
              onClick={() => handleDownloadPDF(incident.id)}
              className="border-slate-700 text-slate-300"
              data-testid="download-pdf-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              PDF
            </Button>
            {mode === 'view' && (
              <Button onClick={() => openEditMode(selectedIncident)} className="bg-sky-600 hover:bg-sky-700">
                <Edit className="w-4 h-4 mr-2" />
                {t('common.edit')}
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => setShowCollaboration(!showCollaboration)}
              className={`border-slate-700 ${showCollaboration ? 'bg-cyan-600/20 border-cyan-600/50 text-cyan-400' : 'text-slate-300'}`}
              data-testid="toggle-collaboration-btn"
            >
              <MessageSquare className="w-4 h-4 mr-2" />
              Collaborate
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Details */}
          <div className="lg:col-span-2 space-y-6">
            <Card className="bg-slate-900/60 border-slate-800">
              <CardHeader>
                <CardTitle className="text-white">Incident Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Title */}
                <div>
                  <Label className="text-slate-500 text-sm">{t('incidents.title')}</Label>
                  {mode === 'edit' ? (
                    <Input
                      value={formData.title || ''}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      className="bg-slate-950 border-slate-800 mt-1 text-lg font-medium"
                      placeholder="Enter incident title..."
                    />
                  ) : (
                    <p className="text-white text-lg font-medium mt-1">{incident.title || 'No title'}</p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-500 text-sm">{t('common.status')}</Label>
                    {mode === 'edit' ? (
                      <Select 
                        value={formData.status || incident.status} 
                        onValueChange={(v) => setFormData({ ...formData, status: v })}
                      >
                        <SelectTrigger className="bg-slate-950 border-slate-800 mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-900 border-slate-800">
                          {STATUSES.map(s => (
                            <SelectItem key={s} value={s}>{t(`incidents.statuses.${s}`)}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Badge className={`${STATUS_COLORS[incident.status]} border mt-1`}>
                        {t(`incidents.statuses.${incident.status}`)}
                      </Badge>
                    )}
                  </div>
                  <div>
                    <Label className="text-slate-500 text-sm">{t('incidents.category')}</Label>
                    {mode === 'edit' ? (
                      <Select 
                        value={formData.occurrence_category || incident.occurrence_category} 
                        onValueChange={(v) => setFormData({ ...formData, occurrence_category: v })}
                      >
                        <SelectTrigger className="bg-slate-950 border-slate-800 mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-900 border-slate-800">
                          {OCCURRENCE_CATEGORIES.map(cat => (
                            <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <p className="text-white mt-1">{incident.occurrence_category}</p>
                    )}
                  </div>
                  <div>
                    <Label className="text-slate-500 text-sm">{t('incidents.occurrenceDate')}</Label>
                    {mode === 'edit' ? (
                      <Input
                        type="date"
                        value={formData.occurrence_date || incident.occurrence_date?.split('T')[0]}
                        onChange={(e) => setFormData({ ...formData, occurrence_date: e.target.value })}
                        className="bg-slate-950 border-slate-800 mt-1"
                      />
                    ) : (
                      <p className="text-white mt-1 flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-slate-500" />
                        {format(new Date(incident.occurrence_date), 'PPP')}
                      </p>
                    )}
                  </div>
                  <div>
                    <Label className="text-slate-500 text-sm">{t('incidents.location')}</Label>
                    {mode === 'edit' ? (
                      <Input
                        value={formData.location || ''}
                        onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                        className="bg-slate-950 border-slate-800 mt-1"
                        placeholder="Enter location..."
                      />
                    ) : (
                      <p className="text-white mt-1">{incident.location}</p>
                    )}
                  </div>
                </div>

                <div>
                  <Label className="text-slate-500 text-sm">{t('incidents.description')}</Label>
                  {mode === 'edit' ? (
                    <Textarea
                      value={formData.description || ''}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      rows={4}
                      className="bg-slate-950 border-slate-800 mt-1"
                      placeholder="Describe the incident..."
                    />
                  ) : (
                    <p className="text-slate-300 mt-1 whitespace-pre-wrap">{incident.description}</p>
                  )}
                </div>

                {(incident.flight_number || incident.aircraft_type || incident.aircraft_registration || incident.aircraft_id || incident.engine_id || incident.component_id) && (
                  <div className="pt-4 border-t border-slate-800 space-y-4">
                    {/* Linked Assets */}
                    {(incident.aircraft_id || incident.engine_id || incident.component_id) && (
                      <div>
                        <Label className="text-slate-500 text-sm flex items-center gap-2">
                          <Plane className="w-4 h-4" />
                          Linked Assets
                        </Label>
                        <div className="mt-2">
                          <AssetDisplay
                            aircraftId={incident.aircraft_id}
                            aircraftRegistration={incident.aircraft_registration}
                            engineId={incident.engine_id}
                            engineSerial={incident.engine_serial}
                            componentId={incident.component_id}
                            componentPartNumber={incident.component_part_number}
                          />
                        </div>
                      </div>
                    )}
                    
                    {/* Flight and Aircraft Info */}
                    <div className="grid grid-cols-3 gap-4">
                      {incident.flight_number && (
                        <div>
                          <Label className="text-slate-500 text-sm">{t('incidents.flightNumber')}</Label>
                          <p className="text-white mt-1">{incident.flight_number}</p>
                        </div>
                      )}
                      {incident.aircraft_type && (
                        <div>
                          <Label className="text-slate-500 text-sm">{t('incidents.aircraftType')}</Label>
                          <p className="text-white mt-1">{incident.aircraft_type}</p>
                        </div>
                      )}
                      {incident.aircraft_registration && !incident.aircraft_id && (
                        <div>
                          <Label className="text-slate-500 text-sm">{t('incidents.aircraftRegistration')}</Label>
                          <p className="text-white mt-1">{incident.aircraft_registration}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Investigation Section */}
            <Card className="bg-slate-900/60 border-slate-800">
              <CardHeader>
                <CardTitle className="text-white">{t('incidents.investigation')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-500 text-sm">{t('incidents.severity')}</Label>
                    {mode === 'edit' ? (
                      <Select 
                        value={formData.severity || incident.severity || ''} 
                        onValueChange={(v) => setFormData({ ...formData, severity: v })}
                      >
                        <SelectTrigger className="bg-slate-950 border-slate-800 mt-1">
                          <SelectValue placeholder="Select severity" />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-900 border-slate-800">
                          {SEVERITIES.map(s => (
                            <SelectItem key={s} value={s}>{t(`incidents.severities.${s}`)}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <p className="text-white mt-1 capitalize">{incident.severity || '-'}</p>
                    )}
                  </div>
                  <div>
                    <Label className="text-slate-500 text-sm">{t('incidents.likelihood')}</Label>
                    {mode === 'edit' ? (
                      <Select 
                        value={formData.likelihood || incident.likelihood || ''} 
                        onValueChange={(v) => setFormData({ ...formData, likelihood: v })}
                      >
                        <SelectTrigger className="bg-slate-950 border-slate-800 mt-1">
                          <SelectValue placeholder="Select likelihood" />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-900 border-slate-800">
                          {LIKELIHOODS.map(l => (
                            <SelectItem key={l} value={l}>{l.replace('_', ' ')}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <p className="text-white mt-1 capitalize">{incident.likelihood?.replace('_', ' ') || '-'}</p>
                    )}
                  </div>
                </div>

                {incident.risk_level && (
                  <div>
                    <Label className="text-slate-500 text-sm">{t('incidents.riskLevel')}</Label>
                    <Badge className={`${RISK_COLORS[incident.risk_level]} px-3 py-1 mt-1 text-sm`}>
                      {t(`risks.levels.${incident.risk_level}`)}
                    </Badge>
                  </div>
                )}

                <div>
                  <Label className="text-slate-500 text-sm">{t('incidents.investigationNotes')}</Label>
                  {mode === 'edit' ? (
                    <Textarea
                      value={formData.investigation_notes || ''}
                      onChange={(e) => setFormData({ ...formData, investigation_notes: e.target.value })}
                      rows={3}
                      className="bg-slate-950 border-slate-800 mt-1"
                      placeholder="Add investigation notes..."
                    />
                  ) : (
                    <p className="text-slate-300 mt-1 whitespace-pre-wrap">{incident.investigation_notes || '-'}</p>
                  )}
                </div>

                <div>
                  <Label className="text-slate-500 text-sm">{t('incidents.rootCause')}</Label>
                  {mode === 'edit' ? (
                    <Textarea
                      value={formData.root_cause || ''}
                      onChange={(e) => setFormData({ ...formData, root_cause: e.target.value })}
                      rows={3}
                      className="bg-slate-950 border-slate-800 mt-1"
                      placeholder="Describe the root cause..."
                    />
                  ) : (
                    <p className="text-slate-300 mt-1 whitespace-pre-wrap">{incident.root_cause || '-'}</p>
                  )}
                </div>

                {mode === 'edit' && (
                  <div className="flex gap-3 pt-4">
                    <Button onClick={handleUpdate} className="bg-sky-600 hover:bg-sky-700">
                      {t('common.save')}
                    </Button>
                    <Button 
                      variant="outline" 
                      onClick={() => setMode('view')}
                      className="border-slate-700 text-slate-300"
                    >
                      {t('common.cancel')}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* AI Classification Sidebar */}
          <div className="space-y-6">
            <Card className="bg-slate-900/60 border-slate-800">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Brain className="w-5 h-5 text-violet-400" />
                  {t('incidents.aiClassification')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {incident.ai_classification && !incident.ai_classification.error ? (
                  <div className="space-y-4">
                    {incident.ai_classification.severity_recommendation && (
                      <div>
                        <Label className="text-slate-500 text-sm">Recommended Severity</Label>
                        <p className="text-white mt-1 capitalize">{incident.ai_classification.severity_recommendation}</p>
                      </div>
                    )}
                    {incident.ai_classification.likelihood_recommendation && (
                      <div>
                        <Label className="text-slate-500 text-sm">Recommended Likelihood</Label>
                        <p className="text-white mt-1 capitalize">{incident.ai_classification.likelihood_recommendation}</p>
                      </div>
                    )}
                    {incident.ai_classification.risk_level && (
                      <div>
                        <Label className="text-slate-500 text-sm">AI Risk Assessment</Label>
                        <Badge className={`${RISK_COLORS[incident.ai_classification.risk_level]} px-3 py-1 mt-1`}>
                          {incident.ai_classification.risk_level}
                        </Badge>
                      </div>
                    )}
                    {incident.ai_classification.summary && (
                      <div>
                        <Label className="text-slate-500 text-sm">AI Summary</Label>
                        <p className="text-slate-300 mt-1 text-sm">{incident.ai_classification.summary}</p>
                      </div>
                    )}
                    {incident.ai_classification.contributing_factors && (
                      <div>
                        <Label className="text-slate-500 text-sm">Contributing Factors</Label>
                        <ul className="mt-1 space-y-1">
                          {incident.ai_classification.contributing_factors.map((factor, i) => (
                            <li key={i} className="text-sm text-slate-400 flex items-start gap-2">
                              <AlertTriangle className="w-3 h-3 mt-1 text-orange-400 flex-shrink-0" />
                              {factor}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {incident.ai_classification.immediate_actions && (
                      <div>
                        <Label className="text-slate-500 text-sm">Recommended Actions</Label>
                        <ul className="mt-1 space-y-1">
                          {incident.ai_classification.immediate_actions.map((action, i) => (
                            <li key={i} className="text-sm text-emerald-400 flex items-start gap-2">
                              <Sparkles className="w-3 h-3 mt-1 flex-shrink-0" />
                              {action}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-6 text-slate-500">
                    <Brain className="w-10 h-10 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No AI analysis available</p>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleRunAI(incident.id)}
                      disabled={aiLoading}
                      className="mt-3 border-violet-600/50 text-violet-400"
                    >
                      Run Analysis
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* ECCAIRS 2 Compatibility Card */}
            <Card className="bg-slate-900/60 border-slate-800">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Globe className="w-5 h-5 text-blue-400" />
                  ECCAIRS 2 (EU Reporting)
                </CardTitle>
              </CardHeader>
              <CardContent>
                {incident.eccairs_data ? (
                  <div className="space-y-4">
                    {incident.eccairs_data.occurrence_categories?.length > 0 && (
                      <div>
                        <Label className="text-slate-500 text-sm">ECCAIRS Categories</Label>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {incident.eccairs_data.occurrence_categories.map((cat, i) => (
                            <Badge key={i} className="bg-blue-600/20 text-blue-400 border border-blue-600/50">
                              {cat}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    {incident.eccairs_data.occurrence_class && (
                      <div>
                        <Label className="text-slate-500 text-sm">Occurrence Class</Label>
                        <p className="text-white mt-1 capitalize">{incident.eccairs_data.occurrence_class?.replace(/_/g, ' ')}</p>
                      </div>
                    )}
                    {incident.eccairs_data.phase_of_flight && (
                      <div>
                        <Label className="text-slate-500 text-sm">Phase of Flight</Label>
                        <p className="text-white mt-1 capitalize">{incident.eccairs_data.phase_of_flight?.replace(/_/g, ' ')}</p>
                      </div>
                    )}
                    <div className="grid grid-cols-2 gap-2">
                      {incident.eccairs_data.highest_injury && (
                        <div>
                          <Label className="text-slate-500 text-xs">Injury Level</Label>
                          <p className="text-white text-sm capitalize">{incident.eccairs_data.highest_injury}</p>
                        </div>
                      )}
                      {incident.eccairs_data.highest_damage && (
                        <div>
                          <Label className="text-slate-500 text-xs">Damage Level</Label>
                          <p className="text-white text-sm capitalize">{incident.eccairs_data.highest_damage}</p>
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2 pt-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleExportECCAIRS(incident.id)}
                        className="border-blue-600/50 text-blue-400 flex-1"
                        data-testid="export-eccairs-btn"
                      >
                        <FileJson className="w-4 h-4 mr-1" />
                        Export E5X
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-6 text-slate-500">
                    <Globe className="w-10 h-10 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No ECCAIRS mapping</p>
                    <p className="text-xs text-slate-600 mt-1">Map to EU taxonomy for reporting</p>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleMapToECCAIRS(incident.id)}
                      disabled={eccairsLoading}
                      className="mt-3 border-blue-600/50 text-blue-400"
                      data-testid="map-eccairs-btn"
                    >
                      {eccairsLoading ? 'Mapping...' : 'Auto-Map to ECCAIRS'}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Collaboration Panel */}
        {showCollaboration && (
          <CollaborationPanel
            entityType="incident"
            entityId={incident.id}
            entityTitle={incident.title}
            apiClient={api}
            currentUser={user}
            token={token}
            isOpen={showCollaboration}
            onClose={() => setShowCollaboration(false)}
          />
        )}
      </div>
    );
  }

  return null;
}
